import * as React from "react"
import trans from 'trans'
import { AxiosResponse } from 'axios';
import { connect } from 'react-redux'

import {
    css,
    Grid,
    GridCell,
    Icon,
    Theme,
    Message,
    Button,
    InputGroup,
    Spinner,
    Section,
    Select,
} from "@bdfx/mergado-ui-kit";

import { ID } from "app/types/app-types";
import { getCustomPalettes, getGradients, getProjectCollageLayouts, getProjectImportPreviews, getProjectsOfUser, getTemplates, importProjectToProject } from "app/helpers/api";
import { CollageLayoutType, CustomImageSetFile, CustomPalette, CustomPalettesFormType, Gradient, Template } from "app/types/model-types";
import { URL } from "app/config/api-config";
import { GradientSvg } from "shape-edit/forms/fields";
import AlternativeImageSvg from "shape-entities/components/alternative-image-svg";
import { setGradientsToStore, setTemplatesToStore } from "template-edit/reducers";
import { initForm } from "app/reducers/form";
import { debounce, find } from "lodash";

type ImportProjectType = {
    id: ID
    shop_id: ID
    type: string
    name: string
    shop_name: string
}

type ProjectPreviewsType = {
    templates: Template[]
    gradients: Gradient[]
    collages: CollageLayoutType[]
    palettes: CustomPalette[]
    images: CustomImageSetFile[]
}

type ImportStatusType = {
    imported_templates: string[]
    imported_layouts: CollageLayoutType[]
    imported_gradients: Gradient[]
    imported_palettes: CustomPalette[]
    imported_images: string[]
}

export type ProjectToProjectImportOption = 'templates' | 'collages' | 'gradients' | 'images' | 'palettes'
export const ProjectToProjectImportOptions = ['templates', 'collages', 'gradients', 'images', 'palettes']

interface Props {
    initForm: typeof initForm
    setTemplatesToStore: typeof setTemplatesToStore
    setGradientsToStore: typeof setGradientsToStore
}

interface State {
    isLoading: boolean
    isLoadingPreviews: boolean
    isImporting: boolean
    availableProjects: ImportProjectType[]
    fromProject: number
    autocomplete: string
    importOptions: ProjectToProjectImportOption[]
    projectPreviews: ProjectPreviewsType
    importStatus: ImportStatusType
}

class ImportFromProject extends React.Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isLoading: null,
            isLoadingPreviews: null,
            isImporting: null,
            availableProjects: null,
            autocomplete: '',
            fromProject: null,
            importOptions: [],
            projectPreviews: null,
            importStatus: null,
        }
    }

    componentDidMount() {
        this.getProjectsOfUser()
    }

    getProjectPreviews = (fromProjectId: number) => {
        this.setState({isLoadingPreviews: true})
        getProjectImportPreviews(fromProjectId)
            .then((response: AxiosResponse<ProjectPreviewsType>) => {
                this.setState({projectPreviews: response.data})
            })
            .catch((error) => {
            console.error('cannot get project previews', error)
            })
        .finally(() => this.setState({isLoadingPreviews: false}))
    }

    getProjectsOfUser = () => {
        this.setState({isLoading: true})
        getProjectsOfUser()
            .then((response: AxiosResponse<ImportProjectType[]>) => {
                this.setState({availableProjects: response.data})
            })
            .catch((error) => {
            console.error('cannot get user projects', error)
            })
        .finally(() => this.setState({isLoading: false}))
    }

    importProjectToProject = () => {
        const { fromProject, importOptions } = this.state
        this.setState({ isImporting: true })

        importProjectToProject(fromProject, importOptions)
            .then((response: AxiosResponse<ImportStatusType>) => {
                this.setState({ importStatus: response.data })

                if (response.data.imported_templates) {
                    getTemplates()
                        .then((response: AxiosResponse<Template[]>) => {
                            this.props.setTemplatesToStore(response.data)
                        })
                        .catch(e => {
                            console.error('error white getting templates', e.response ?? null)
                        })
                        .finally(() => {
                            this.setState({ isLoading: false })
                        })
                }

                if (response.data.imported_layouts) {
                    getProjectCollageLayouts()
                        .then((response: AxiosResponse<CollageLayoutType[]>) => {
                            this.props.initForm('collageLayoutsForm', response.data)
                        })
                        .catch(e => {
                            console.error('error white getting templates', e.response ?? null)
                        })
                        .finally(() => {
                            this.setState({ isLoading: false })
                        })
                }

                if (response.data.imported_gradients) {
                    getGradients()
                        .then((response: AxiosResponse<Gradient[]>) => {
                            this.props.setGradientsToStore(response.data)
                        })
                        .catch(error => {
                            console.error('cannot set gradients', error)
                        })
                        .finally(() => {
                            this.setState({ isLoading: false })
                        })
                }

                if (response.data.imported_palettes) {
                    getCustomPalettes()
                        .then((response: AxiosResponse<CustomPalettesFormType>) => {
                            this.props.initForm('customPalettesForm', response.data)
                        })
                        .catch(error => {
                            console.error('cannot set palettes', error)
                        })
                }

                if (response.data.imported_images) {
                    //TODO: custom image set
                }
            })
            .catch((error) => {
                console.error('cannot import from project', error)
            })
            .finally(() => this.setState({ isImporting: false }))
    }

    renderCollage(c: CollageLayoutType, i: number, width: number, height: number): JSX.Element {
        return <GridCell key={`collage_layout_${c.id}_${i}`}>
            <svg
                id="collage-layout-selected"
                xmlns="http://www.w3.org/2000/svg"
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(100vh - 110px)',
                    width: 'auto',
                    height: 'auto',
                    overflow: 'auto',
                    marginTop: '10px',
                }}>

                <rect id="collage-layout-selected-box" fill={'#ffffff'} x={0} y={0} width={width} height={height} style={{ pointerEvents: 'none' }} />
                <rect stroke={'#3a3a3a'} x={0} y={0} width={width} height={height} fill={'transparent'} style={{ pointerEvents: 'auto' }} />

                <AlternativeImageSvg
                    draggable={false}
                    layoutWidth={width}
                    layoutHeight={height}
                    selectedCollage={c}
                    isShadow={false}
                    isAnim={false}
                    previewMode={true}
                    randomProduct={{
                        id: 0,
                        serialized_data: undefined,
                        image_id: 0,
                        image_link: "",
                        isAlt: null,
                        bgremovedImage: "",
                        imageBlob: "",
                        error: "",
                        status_code: 0,
                        palette: [],
                        dominantColor: "",
                        alternative_images: [],
                        origWidth: 0,
                        origHeight: 0,
                        width: 0,
                        height: 0,
                        image_hash: "",
                        isLoading: false,
                        isLoaded: false,
                        isError: false,
                    }}
                />
            </svg>
        </GridCell>
    }

    handleChangeAutocomplete = (fromProject: number) => {
        const { availableProjects } = this.state
        const item = find(availableProjects, (p) => {
            return `${fromProject}` === `${p.id}`
        })

        if (item) {
            //const o = `${p.id} ${p.name} ${p.shop_name} ${p.name}`
                this.setState({
                    fromProject: fromProject,
                })
            this.getProjectPreviews(fromProject)
        }

        /* const item = find(availableProjects, (p) => {
            const o = `${p.id} ${p.name} ${p.shop_name} ${p.name}`
            return newValue === o.toLowerCase()
        })

        if (item) {
            const fromProject = parseInt((newValue.split(' '))[0])
            if (typeof fromProject !== 'undefined') {
                this.setState({
                    fromProject: fromProject,
                    autocomplete: newValue,
                })
                this.getProjectPreviews(fromProject)
            }
        } */
    }


    render() {
        const { availableProjects, fromProject, autocomplete, isLoading, isLoadingPreviews, importOptions, projectPreviews, importStatus } = this.state

        let importOptionsItems = []
        if (projectPreviews) {
            if (projectPreviews.templates && projectPreviews.templates.length > 0) {
                importOptionsItems.push({ value: 'templates', label: trans('templates') })
            }
            if (projectPreviews.collages && projectPreviews.collages.length > 0) {
                importOptionsItems.push({ value: 'collages', label: trans('collage_layouts') })
            }
            if (projectPreviews.gradients && projectPreviews.gradients.length > 0) {
                importOptionsItems.push({ value: 'gradients', label: trans('gradients') })
            }
            if (projectPreviews.images && projectPreviews.images.length > 0) {
                importOptionsItems.push({ value: 'images', label: trans('images_from_upload') })
            }
            if (projectPreviews.palettes && projectPreviews.palettes.length > 0) {
                importOptionsItems.push({ value: 'palettes', label: trans('brand_colors') })
            }
        }

        const options = availableProjects && availableProjects.length > 0 ? availableProjects.map((p: ImportProjectType) => {
            const o = `${p.id} ${p.name} ${p.shop_name} ${p.name}`
            return {
                label: o,
                text: o,
                value: `${p.id}`,
            }
        }) : null

        return <StyledContent>
            {options ?
                <React.Fragment>
                    <GridCell>
                        {isLoading && <Spinner type={'bubbles'} />}

                        <Select
                            value={autocomplete}
                            multiple={false}
                            options={options}
                            search={true}
                            searchPlaceholder={trans('choose_project')}
                            onSearch={(value) => this.setState({ autocomplete: value })}
                            onChange={(fromProject: number) => this.handleChangeAutocomplete(fromProject)}
                            onClear={() => this.setState({ autocomplete: null, fromProject: null, projectPreviews: null })}
                            closeOnChange={true}
                            floatStyles={{
                                minWidth: '350px !important',
                            }}
                            className='mergado-element-select'
                        />

                        <InputGroup
                            orientation={"vertical"}
                            disabled={!fromProject}
                            items={importOptionsItems}
                            value={importOptions}
                            onChange={(value) => this.setState({ importOptions: value })}
                        />

                        <Button
                            type={'void'}
                            secondary={false}
                            disabled={!fromProject || !importOptions || !importOptions.length}
                            icon={<Icon type="download" text={trans('import')} />}
                            onClick={() => this.importProjectToProject()}
                            style={{ marginBottom: '40px' }}
                        />


                        {importStatus &&
                            <Message type={'info'} icon={<Icon type="info-circle" color={Theme.steelblue} size={30} />}>
                                <Grid cols={'auto'}>
                                    {importStatus.imported_templates &&
                                        <GridCell>
                                            <h3>{trans('templates')}</h3>
                                            <ul>
                                                {importStatus.imported_templates.map((i, j) => <li key={`imported_templates_${j}`}>{i}</li>)}
                                            </ul>
                                        </GridCell>
                                    }
                                </Grid>


                                <Grid cols={'auto'}>
                                    {importStatus.imported_layouts &&
                                        <GridCell>
                                            <h3>{trans('collage_layouts')}</h3>
                                            <Grid cols={'repeat(5, 1fr)'}>
                                                {importStatus.imported_layouts.map((c, i) => this.renderCollage(c, i, 100, 100))}
                                            </Grid>
                                        </GridCell>
                                    }
                                </Grid>

                                <Grid cols={'auto'}>
                                    {importStatus.imported_gradients &&
                                        <GridCell>
                                            <h3>{trans('gradients')}</h3>
                                            {importStatus.imported_gradients.map((g, j) => <span key={`imported_gradients_${j}`}>
                                                <GradientSvg gradient={g} idx={g.id} width={100} height={100} isActive={false} style={{ marginBottom: '-20px' }} />
                                            </span>)}
                                        </GridCell>
                                    }
                                </Grid>

                                <Grid cols={'auto'}>
                                    {importStatus.imported_images &&
                                        <GridCell>
                                            <h3>{trans('images_from_upload')}</h3>
                                            {importStatus.imported_images.map((i, j) => <img className={'template-preview'} src={i} key={`imported_images_${j}_${i}`} />)}
                                        </GridCell>
                                    }
                                </Grid>

                                <Grid cols={'auto'}>
                                    {importStatus.imported_palettes &&
                                        <GridCell>
                                            <h3>{trans('brand_colors')}</h3>
                                            {importStatus.imported_palettes.map((p, j) =>
                                                <StyledPalettesGrid cols={'104px 104px auto 104px'} key={`imported_palettes_${j}_${p.id}`}>
                                                    <GridCell>{p.colors.map((color: string, colorIdx: number) => {
                                                        return <span key={`palette_${p.id}_${color}_${colorIdx}`}>
                                                            <div className={`color-sample`} style={{ background: color }}></div>
                                                        </span>
                                                    })}</GridCell>
                                                    <GridCell className={'palette-image'}>{p.image_url && <img src={p.image_url} />}</GridCell>
                                                    <GridCell className={'palette-name'}>{p.name}</GridCell>
                                                </StyledPalettesGrid>
                                            )}
                                        </GridCell>
                                    }
                                </Grid>
                            </Message>
                        }
                    </GridCell>
                    <GridCell style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {isLoadingPreviews && <Spinner type={'bubbles'} />}

                        {(importOptions.includes('templates') && projectPreviews?.templates && projectPreviews.templates.length > 0) &&
                            <Section header={trans('templates')}>
                                <PreviewsGrid>
                                    {projectPreviews.templates.map(t => {
                                        return <GridCell key={`template_${t.id}`}>
                                            <div style={{ height: '100px' }}>
                                                <img
                                                    className={'template-preview'}
                                                    src={`${URL.laravelBase}images/thumbs/thumbnail${t.id}.png`}
                                                    onError={(event) => {
                                                        if (event.currentTarget.parentNode) {
                                                            //event.currentTarget.parentNode.removeChild(event.currentTarget)
                                                            event.currentTarget.src = `https://cdn.bdfx.cz/muk/icons/eye-none-radix.svg`;
                                                            event.currentTarget.width = 80;
                                                            event.currentTarget.height = 80;
                                                            event.currentTarget.style.filter = "brightness(0) saturate(100%) invert(40%) sepia(75%) saturate(0%) brightness(180%) contrast(100%)";
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div style={{
                                                textDecoration: t.active ? undefined : 'line-through',
                                                color: t.active ? '#3a3a3a' : '#ddd',
                                                overflow: 'hidden',
                                                height: '20px',
                                                fontSize: '10px'
                                            }}>{t.name}</div>
                                        </GridCell>
                                    })}
                                </PreviewsGrid>
                            </Section>
                        }


                        {(importOptions.includes('collages') && projectPreviews?.collages && projectPreviews.collages.length > 0) &&
                            <Section header={trans('collage_layouts')}>
                                <Grid cols={'repeat(5, 1fr)'}>
                                    {projectPreviews.collages.map((c, i) => this.renderCollage(c, i, 100, 100))}
                                </Grid>
                            </Section>
                        }

                        {(importOptions.includes('gradients') && projectPreviews?.gradients && projectPreviews.gradients.length > 0) &&
                            <Section header={trans('gradients')}>
                                <PreviewsGrid>
                                    {projectPreviews.gradients.map(g => {
                                        return <GridCell key={`gradient_${g.id}`}>
                                            <GradientSvg gradient={g} idx={g.id} width={100} height={100} isActive={false} style={{ marginBottom: '-20px' }} />
                                        </GridCell>
                                    })}
                                </PreviewsGrid>
                            </Section>
                        }

                        {(importOptions.includes('images') && projectPreviews?.images && projectPreviews.images.length > 0) &&
                            <Section header={trans('images_from_upload')}>
                                <PreviewsGrid>
                                    {projectPreviews.images.map((i: CustomImageSetFile) => <GridCell key={i.id}>
                                        <img className={'template-preview'} src={`${URL.laravelBase}customsets/${fromProject}/${i.set_id}/${i.filename}`} />
                                    </GridCell>
                                    )}
                                </PreviewsGrid>
                            </Section>
                        }

                        {(importOptions.includes('palettes') && projectPreviews?.palettes && projectPreviews.palettes.length > 0) &&
                            <Section header={trans('brand_colors')}>
                                {projectPreviews.palettes.map((customPalette: CustomPalette, paletteIdx: number) => {
                                    return <StyledPalettesGrid cols={'104px 104px auto 104px'} key={`g_${customPalette.id}`}>
                                        <GridCell>{customPalette.colors.map((color: string, colorIdx: number) => {
                                            return <span key={`palette_${customPalette.id}_${color}_${colorIdx}`}>
                                                <div className={`color-sample`} style={{ background: color }}></div>
                                            </span>
                                        })}</GridCell>
                                        <GridCell className={'palette-image'}>{customPalette.image_url && <img src={customPalette.image_url} />}</GridCell>
                                        <GridCell className={'palette-name'}>{customPalette.name}</GridCell>
                                    </StyledPalettesGrid>
                                })
                                }
                            </Section>
                        }
                    </GridCell>
                </React.Fragment> :
                <GridCell col={'1/-1'}>
                    <Message type="info" icon={'info-circle'} text={trans('no_other_projects_of_user')} />
                </GridCell>
            }
        </StyledContent>
    }
}

const StyledPalettesGrid = css(Grid)({
    ' .muk-gridcell': {
        alignSelf: 'center',
        padding: '0',
    },
    ' .palette-image img': {
        maxWidth: '100px',
        maxHeight: '100px',
    },
    ' .color-sample': {
        width: '25px',
        height: '25px',
        float: 'left',
        border: '2px solid transparent',
    },
})

const StyledContent = css(Grid)({
    gridTemplateColumns: '50% 50%',
    gridGap: '20px',
    padding: '20px 0',
    '@media screen and (max-width: 700px)': {
        gridTemplateColumns: 'auto',
    },
    ' .muk-select': {
        textAlign: 'left',
    },
    ' li': {
        textAlign: 'left',
    }
})

const PreviewsGrid = css(Grid)({
    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    gridGap: '10px',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
        border: `1px solid ${Theme.grey}`,
        borderRadius: '4px',
        padding: '10px',
        ':hover': {
            backgroundColor: '#eeeeee',
        }
    },
    ' img.template-preview': {
        maxWidth: '100px',
        maxHeight: '100px',
    },
})

function mapStateToProps(state) {
    return {
    }
}

export default connect(mapStateToProps, {
    initForm,
    setTemplatesToStore,
    setGradientsToStore,
})(ImportFromProject)
